<template>
  <div v-if="loading" class="filters-container">
    Loading data...
  </div>
  <div v-else class="filters-container">
    <filter-item
      v-for="filterData in filtersData"
      :key="filterData.name"
      :dropdownOptions="dropdownOptions"
      :filter="filterData"
      :value="filter[filterData.name]"
      @input="onFilterInput"
    />
    <button class="white small" @click="toggleAdvancedFilters">Advanced Filters</button>
    <div class="filters-collapse" :class="{ open: showAdvancedFilters }">
      <filter-item
        v-for="filterData in advancedFiltersData"
        :key="filterData.name"
        :dropdownOptions="dropdownOptions"
        :filter="filterData"
        :value="filter[filterData.name]"
        @input="onFilterInput"
      />
    </div>
    <div class="pagination-wrapper">
      <pagination :currentPage="parseInt(filter.page)" :totalPages="pages" @go-to-page="goToPage" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, watchEffect } from 'vue';

import FilterItem from '@/components/FilterItem.vue';
import Pagination from '@/components/Pagination/index.vue';
import { filtersData, advancedFiltersData } from '@/data';
import { IFilter, IIssueCategory, IIssueType } from '@/types';
import { useTaxonomy, useClaims } from '@/hooks';
import { Filter, FILTER_KEY } from '@/hooks/useFilter';
import { injectOrThrow } from '@/utils';

export default defineComponent({
  name: 'Filters',
  components: {
    FilterItem,
    Pagination,
  },
  setup() {
    /**
     * Filter state; provided in Home.vue
     */
    const _filter: Filter = injectOrThrow(FILTER_KEY);

    const showAdvancedFilters = ref<boolean>(false);

    const {
      loading,
      categories,
      publicResolutions,
      suppliers,
      tags,
      severity,
      priorities,
      getCategoryTypes,
      getCategoryTypeIssues,
      error,
    } = useTaxonomy();
    const { pages } = useClaims();

    const dropdownOptions = reactive({
      claimCategoryOptions: categories,
      supplierOptions: suppliers,
      resolutionTypeOptions: publicResolutions,
      claimIssueCategoryOptions: [] as IIssueCategory[],
      claimIssueTypeOptions: [] as IIssueType[],
      claimDefectSeverityOptions: severity,
      claimPriorityOptions: priorities,
      claimTagOptions: tags,
      claimFlagOptions: [{ id: true, name: 'Flagged Only' }],
      claimProductQualityIssueOptions: [
        { id: true, name: 'Yes' },
        { id: false, name: 'No' },
      ],
    });

    // auto update dropdown options that have dependency
    // reset issueCategory and issueType if category is changed
    watchEffect(() => {
      if (_filter.filter.claimCategory) {
        dropdownOptions.claimIssueCategoryOptions = getCategoryTypes(
          parseInt(_filter.filter.claimCategory)
        );

        if (_filter.filter.claimIssueCategory) {
          dropdownOptions.claimIssueTypeOptions = getCategoryTypeIssues(
            parseInt(_filter.filter.claimCategory),
            parseInt(_filter.filter.claimIssueCategory)
          );
        } else {
          dropdownOptions.claimIssueTypeOptions = [];
          _filter.filter.claimIssueType = '';
        }
      } else {
        dropdownOptions.claimIssueCategoryOptions = [];
        dropdownOptions.claimIssueTypeOptions = [];
        _filter.filter.claimIssueCategory = '';
        _filter.filter.claimIssueType = '';
      }
    });

    const onFilterInput = (value: string, fieldName: string) => {
      _filter.updateFilter(fieldName as keyof IFilter, value);
    };

    const toggleAdvancedFilters = () => {
      showAdvancedFilters.value = !showAdvancedFilters.value;
    };

    const goToPage = (pageNumber: number) => {
      _filter.filter.page = pageNumber.toString();
    };

    return {
      filtersData,
      advancedFiltersData,
      filter: _filter.filter,
      dropdownOptions,
      showAdvancedFilters,
      toggleAdvancedFilters,
      goToPage,
      loading,
      error,
      pages,
      onFilterInput,
    };
  },
});
</script>

<style lang="scss">
.filters-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: var(--space-4) var(--space-2);

  button {
    margin-top: var(--space-6);
  }

  .filters-collapse {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    overflow: hidden;
    max-height: 0;
    transition: all 0.5s ease-in-out;
    max-width: 1450px;

    &.open {
      max-height: 50rem;
    }
  }

  .pagination-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: var(--space-4);
  }
}
</style>
